"use client";
import { Form, FormInstance, Input, Select } from "antd";
import { RcFile, UploadChangeParam, UploadFile, UploadProps } from "antd/es/upload";
import dayjs from "dayjs";
import alertUtils from "@/utils/alert";
import type { DatePickerProps } from "antd";
import { LoadingOutlined, PlusOutlined } from "@ant-design/icons";
import React, { useEffect, useState } from "react";
import { message, Upload, DatePicker } from "antd";
const { TextArea } = Input;
import advancedFormat from "dayjs/plugin/advancedFormat";
import customParseFormat from "dayjs/plugin/customParseFormat";
import localeData from "dayjs/plugin/localeData";
import weekday from "dayjs/plugin/weekday";
import { useAuth } from "@/hooks";
import weekOfYear from "dayjs/plugin/weekOfYear";
import weekYear from "dayjs/plugin/weekYear";
import Image from "next/image";
import { definitions } from "@/services/metruyenvl";
import userAPI from "@/services/user_api";
import uploadAPI from "@/services/upload_api";
import { ResponseTypes } from "@/types/constants";
import { useDispatch } from "react-redux";
import { AppDispatch } from "@/redux/store";
import { setUser } from "@/redux/reducers/user.reducer";

dayjs.extend(customParseFormat);
dayjs.extend(advancedFormat);
dayjs.extend(weekday);
dayjs.extend(localeData);
dayjs.extend(weekOfYear);
dayjs.extend(weekYear);

function UserInfo({ user }: { user: definitions["do.UserDO"] }) {
    // const formRef = React.useRef<FormInstance>(null);
    const dispatch = useDispatch<AppDispatch>();
    const [form] = Form.useForm();
    const { userInfo } = useAuth();
    const [currentUser, setCurrentUser] = useState<definitions["do.UserDO"]>({});
    const [disableBtn, setDisableBtn] = useState<boolean>(false);
    const [dateBirth, setDateBirth] = useState<any>(10000);
    const [loading, setLoading] = useState(false);
    const [imageUrl, setImageUrl] = useState<string>(user.avatar as string);
    const updateInfor = async () => {
        try {
            const res: ResponseTypes = await userAPI.getInfo(user.id ?? "");
            const data: definitions["do.UserDO"] = res.data;
            setCurrentUser(data);
            dispatch(setUser(data));
            form.setFieldsValue(data);
            setImageUrl(data.avatar ?? "/avatar.gif");
            setDateBirth(data.birth_time ?? 10000);
        } catch (error) {
            console.log(error);
        }
    };
    const handleChange: UploadProps["onChange"] = async (info: UploadChangeParam<UploadFile>) => {
        setImageUrl("");
        const uploadLink: definitions["do.UploadedPartReq"] = {
            id: "",
            upload_id: "",
            uploaded_parts: [
                {
                    etag: "",
                    part_idx: 1,
                },
            ],
        };

        if (info.file.status === "uploading") {
            setLoading(true);
            return;
        }

        if (info.file.status === "done") {
            try {
                const file: definitions["do.UploadReq"] = {
                    ext: info.file.name.split(".").pop(),
                    file_size: info.file.size as number,
                    mime_type: info.file.type,
                    name: info.file.name,
                };

                const res: definitions["do.UploadFileResp"] = await uploadAPI.upload(file);
                uploadLink.id = res?.id as string;
                uploadLink.upload_id = res?.upload_id as string;

                const linkUpload = res?.upload_links?.[0].link as string;

                const uploadAws = await uploadAPI.uploadToAWS(linkUpload, info.file.originFileObj);

                uploadLink.uploaded_parts[0].etag = JSON.parse(uploadAws.headers["etag"]);
                const links: definitions["do.DocumentResp"] = await uploadAPI.uploadCompleted(uploadLink);
                setImageUrl(links?.link_cdn ?? "");
                setLoading(false);

                return;
            } catch (error) {
                console.log("UserInfo - handleChange: ", error);
                setImageUrl(currentUser?.avatar ?? "");
                setLoading(false);
            }
        }
        if (info.file.status === "error") {
            setImageUrl(currentUser?.avatar ?? "");
        }
    };
    const onFinish = async (values: any) => {
        try {
            const data = {
                ...values,
                birth_time: dateBirth,
                avatar: imageUrl,
            };
            const res = await userAPI.updateUser(data);
            updateInfor();
            alertUtils.toastSuccess("Cập nhật thông tin thành công");
        } catch (error) {
            console.log(error);
        }
    };

    const onFinishFailed = (errorInfo: any) => {
        console.log("Failed:", errorInfo);
    };
    // const getBase64 = (img: RcFile, callback: (url: string) => void) => {
    //     const reader = new FileReader();
    //     reader.addEventListener("load", () => callback(reader.result as string));
    //     reader.readAsDataURL(img);
    // };

    const beforeUpload = (file: RcFile) => {
        const isJpgOrPng = file.type === "image/jpeg" || file.type === "image/png" || file.type === "image/jpg";
        if (!isJpgOrPng) {
            message.error("Bạn chỉ có thể tải lên tệp JPG/PNG!");
        }
        const isLt2M = file.size / 1024 / 1024 < 2;
        if (!isLt2M) {
            message.error("Hình ảnh phải nhỏ hơn 2MB!");
        }

        return isJpgOrPng && isLt2M;
    };

    useEffect(() => {
        // form.setFieldsValue(user);
        updateInfor();
        if (userInfo.id == user.id) {
            setDisableBtn(false);
        } else {
            setDisableBtn(true);
        }
        return () => {
            form.resetFields();
        };
    }, []);
    const changeDate: DatePickerProps["onChange"] = (date: any, dateString: any) => {
        if (dateString) {
            const timestamp = new Date(dateString).getTime() / 1000;
            setDateBirth(timestamp);
        } else {
            setDateBirth(null);
        }
    };
    const uploadButton = (
        <button style={{ border: 0, background: "none" }} type="button">
            {loading ? <LoadingOutlined /> : <PlusOutlined />}
            <div style={{ marginTop: 8 }}>Upload</div>
        </button>
    );

    return (
        <div>
            <Form name="user" form={form} className="md:ml-6 flex flex-col items-center" labelCol={{ span: 8 }} wrapperCol={{ span: 16 }} style={{ maxWidth: 600 }} initialValues={currentUser} onFinish={onFinish} onFinishFailed={onFinishFailed} autoComplete="off">
                <div>
                    <label>Ảnh hiển thị</label>
                    <Upload name="avatar" listType="picture-circle" className="avatar-uploader" disabled={disableBtn} showUploadList={false} beforeUpload={beforeUpload} onChange={handleChange}>
                        {imageUrl ? <Image unoptimized={true} src={imageUrl ?? "/avatar.gif"} className="h-24 w-24 overflow-hidden rounded-full object-cover" width={120} height={120} alt="avatar" style={{ width: "100%" }} /> : uploadButton}
                    </Upload>
                    <label>Email</label>
                    <Form.Item
                        name="email"
                        rules={[
                            {
                                type: "email",
                                required: true,
                                message: "Vui lòng nhập email và đúng định dạng!",
                            },
                        ]}
                    >
                        <Input className="rounded-full h-10 border border-solid border-slate-200 mt-1 w-80" disabled={true} />
                    </Form.Item>
                    <label>Tên hiển thị</label>
                    <Form.Item
                        name="name"
                        rules={[
                            {
                                required: true,
                                message: "Vui lòng nhập tên hiển thị!",
                            },
                        ]}
                    >
                        <Input className="rounded-full h-10 border border-solid border-slate-200 mt-1 w-80" disabled={disableBtn} />
                    </Form.Item>
                    <label>Năm sinh</label>
                    <div className="mb-4">
                        <DatePicker format={"YYYY-MM-DD"} onChange={changeDate} value={dateBirth ? dayjs(dayjs.unix(dateBirth), "YYYY-MM-DD") : null} disabled={disableBtn} />
                    </div>
                    <label>Giới tính</label>
                    <Form.Item name="sex">
                        <Select
                            autoFocus={false}
                            options={[
                                { value: "male", label: "Nam" },
                                { value: "female", label: "Nữ" },
                                { value: "unknow", label: "Khác" },
                            ]}
                            disabled={disableBtn}
                        />
                    </Form.Item>
                    <label>Mô tả</label>
                    <Form.Item name="bio">
                        <TextArea disabled={disableBtn} className="max-w-none w-80 h-10 border border-solid border-slate-200 mt-1" />
                    </Form.Item>
                    {!disableBtn && (
                        <button className="float-right mb-4 mt-4 bg-orange-400 rounded-lg p-2 text-white font-semibold" typeof="submit">
                            Cập nhật
                        </button>
                    )}
                </div>
            </Form>
        </div>
    );
}

export default UserInfo;
